import {VisuallyHidden} from '@commercial-helios/ui/src/components/visually-hidden'
import {tokens, colors} from '@commercial-helios/ui/theme'
import React from 'react'
import styled from 'styled-components'

import Button from '@helios/shared/components/button'
import ExternalLink from '@helios/shared/components/external-link'
import Image from '@helios/shared/components/image'
import type {Props as PrismicLinkProps} from '@helios/shared/components/prismic-link'
import PrismicLink from '@helios/shared/components/prismic-link'
import type {Props as RichTextProps} from '@helios/shared/components/rich-text'
import RichText, {hasRichText} from '@helios/shared/components/rich-text'
import {Linkedin} from '@helios/shared/images/icons/linkedin'
import {Text, TextLink} from '@helios/shared/styles/typography'
import type {ImageSource} from '@helios/shared/types/image'
import mq from '@helios/shared/utils/media-query'

type TagType = {
    text: string
    color: TagProps['$color']
}

export interface Props {
    image?: ImageSource
    title: string
    titleAs?: 'h1' | 'h2' | 'h3'
    body?: RichTextProps['content']
    ctaText?: string
    ctaLink?: string | PrismicLinkProps
    className?: string
    tag?: TagType
    dataTestId?: string
    ctaAsButton?: boolean
    justifyImage?: 'start' | 'center' | 'end'
}

const Card = ({
    image,
    title,
    titleAs,
    body,
    ctaText,
    ctaLink,
    className,
    tag,
    dataTestId,
    ctaAsButton,
    justifyImage = 'center',
}: Props) => {
    const isPrismicLink = typeof ctaLink !== 'string'
    const hasCta = !!ctaLink && !!ctaText

    const renderCta = React.useMemo(() => {
        const ctaLinkProps = isPrismicLink ? ctaLink : {href: ctaLink}
        const linkedInUrlPattern = /^(https?:\/\/)?(www\.)?linkedin\.com\/.*$/
        const showLinkedinIcon = !isPrismicLink && linkedInUrlPattern.test(ctaLink)

        if (showLinkedinIcon) {
            return (
                <ExternalLink to={ctaLink}>
                    <SocialIcon as={Linkedin} width={40} height={40} aria-hidden />
                    <VisuallyHidden>{ctaText}</VisuallyHidden>
                </ExternalLink>
            )
        }

        if (!hasCta) {
            return null
        }

        if (ctaAsButton) {
            return (
                <ButtonLink
                    variant="outline"
                    forwardedAs={isPrismicLink ? PrismicLink : 'a'}
                    {...ctaLinkProps}
                    {...(dataTestId && {
                        'data-testid': `${dataTestId}-link`,
                    })}
                >
                    {ctaText}
                </ButtonLink>
            )
        }

        return (
            <TextLink
                variant="paragraph-base"
                as={isPrismicLink ? PrismicLink : 'a'}
                {...ctaLinkProps}
                {...(dataTestId && {
                    'data-testid': `${dataTestId}-link`,
                })}
            >
                {ctaText} →
            </TextLink>
        )
    }, [ctaAsButton, ctaLink, ctaText, dataTestId, hasCta, isPrismicLink])

    return (
        <Wrapper
            hasImage={!!image}
            className={className}
            {...(dataTestId && {'data-testid': dataTestId})}
        >
            {!!image && <Image image={image} style={{justifySelf: justifyImage}} />}

            <Content>
                <TextWrapper>
                    <Text as={titleAs}>{title}</Text>

                    {hasRichText(body) && (
                        <RichText variant="paragraph-base" content={body} color="muted" />
                    )}
                </TextWrapper>

                {(tag || ctaLink) && (
                    <TagAndLink>
                        {tag && (
                            <Tag $color={tag.color}>
                                <Text as="span" variant="label-base" color="muted">
                                    {tag.text}
                                </Text>
                            </Tag>
                        )}

                        {renderCta}
                    </TagAndLink>
                )}
            </Content>
        </Wrapper>
    )
}

const Wrapper = styled.div<{hasImage: boolean}>`
    display: grid;
    gap: ${tokens.spacing20};
    padding: ${tokens.spacing20};
    background: ${colors.background.default};
    border-radius: ${tokens.arc8};
    grid-template-rows: ${(props) => (props.hasImage ? 'auto 1fr' : 'auto')};

    ${mq.min('lg')} {
        padding: ${tokens.spacing40};
    }
`

const Content = styled.div`
    display: grid;
    grid-template-rows: auto 1fr;
    gap: ${tokens.spacing10};
    justify-items: start;

    > *:last-child {
        align-self: end;
    }
`

const TextWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing4};
`

type TagProps = {
    $color: keyof typeof colors.tag
}

const Tag = styled.div<TagProps>`
    padding: ${tokens.spacing4} ${tokens.spacing8};
    background: ${(props) => colors.tag[props.$color]};
    border-radius: ${tokens.arc4};
`

const TagAndLink = styled.div`
    display: grid;
    row-gap: ${tokens.spacing24};
    justify-items: start;
`

const ButtonLink = styled(Button)`
    margin-top: ${tokens.spacing28};
`

const SocialIcon = styled.div`
    color: ${tokens.shade700};
    transition: ${tokens.fastIn};
    transition-property: color;

    a:hover & {
        color: ${tokens.shade900};
    }
`

export default Card
